import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import axios from "axios";
import {useUser} from "./UserContext";
import styles from "../designs/ProfilePage.module.css";
import {isDemoModeEnabled} from "../utils/demoAccess";

const safeArray = (value) => Array.isArray(value) ? value : [];
const INVENTORY_HISTORY_STATUSES = new Set(["used", "completed", "canceled", "expired"]);

const SHOP_ITEM_VISUALS = {
    extra_live_bet_today: {
        artClass: "shopItemArtLive",
        glyph: "+1",
        badge: "LIVE",
    },
    extended_match_analysis: {
        artClass: "shopItemArtAnalysis",
        glyph: "AI",
        badge: "DEEP",
    },
    voucher_1: {
        artClass: "shopItemArtVoucher",
        glyph: "V",
        badge: "VIP",
    },
};

const getShopItemVisual = (itemKey) => SHOP_ITEM_VISUALS[itemKey] || {
    artClass: "shopItemArtDefault",
    glyph: "★",
    badge: "ITEM",
};

const formatLedgerReason = (entry) => {
    const reason = entry?.reason || "";
    if (reason === "referral_bonus") return "Друг у Сім’ї";
    if (reason === "daily_login") return "Щоденний бонус";
    if (reason === "shop_purchase") return "Покупка в магазині";
    if (reason === "shop_refund") return "Повернення за покупку";
    if (reason === "admin_test_bonus") return "Тестовий адмін-бонус";
    if (reason === "legacy_balance") return "Старий баланс";
    return reason || "Рух балів";
};

const formatInventoryStatus = (status) => {
    if (status === "available") return "В інвентарі";
    if (status === "active") return "Активовано";
    if (status === "used") return "Використано";
    if (status === "pending_admin") return "Очікує видачі";
    if (status === "completed") return "Виконано";
    if (status === "canceled") return "Скасовано";
    if (status === "expired") return "Протерміновано";
    return status || "—";
};

const formatInventoryDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("uk-UA", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};

export default function ShopPage() {
    const {user, setUser} = useUser();
    const navigate = useNavigate();
    const [activeUser, setActiveUser] = useState(user);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [pointsInfo, setPointsInfo] = useState({
        balance: 0,
        ledger: [],
        purchases: [],
        inventory: [],
        inventory_history: [],
    });
    const [shopItems, setShopItems] = useState([]);
    const [activeTab, setActiveTab] = useState("shop");
    const [shopLoading, setShopLoading] = useState(false);
    const [shopMessage, setShopMessage] = useState("");
    const [copyStatus, setCopyStatus] = useState("");

    const inventoryItems = useMemo(() => {
        return safeArray(pointsInfo.inventory).filter((item) => !INVENTORY_HISTORY_STATUSES.has(item.status));
    }, [pointsInfo.inventory]);
    const inventoryHistoryItems = useMemo(() => {
        const explicitHistory = safeArray(pointsInfo.inventory_history);
        if (explicitHistory.length) return explicitHistory;
        return safeArray(pointsInfo.inventory).filter((item) => INVENTORY_HISTORY_STATUSES.has(item.status));
    }, [pointsInfo.inventory, pointsInfo.inventory_history]);
    const isAdmin = (activeUser?.role || "").toLowerCase() === "admin";
    const shopItemByKey = useMemo(() => {
        return (shopItems || []).reduce((acc, item) => {
            acc[item.key] = item;
            return acc;
        }, {});
    }, [shopItems]);

    useEffect(() => {
        if (isDemoModeEnabled()) {
            navigate("/access-request", {replace: true});
            return;
        }

        let currentUser = user;
        const cachedUserRaw = sessionStorage.getItem("telegramUser") || localStorage.getItem("telegramUser");
        if (!currentUser && cachedUserRaw) {
            try {
                currentUser = JSON.parse(cachedUserRaw);
                setUser(currentUser);
            } catch {
                currentUser = null;
            }
        }

        if (!currentUser?.telegram_id) {
            setError("Користувач не знайдений. Відкрийте магазин через Telegram.");
            setLoading(false);
            return;
        }
        if (!currentUser.has_access && currentUser.role !== "admin") {
            navigate("/access-request", {replace: true});
            return;
        }

        setActiveUser(currentUser);

        const loadShop = async () => {
            try {
                const API_BASE = process.env.REACT_APP_API_BASE || "/api";
                const [pointsResult, shopResult] = await Promise.all([
                    axios.get(`${API_BASE}/user/${currentUser.telegram_id}/points`),
                    axios.get(`${API_BASE}/shop/items`),
                ]);
                setPointsInfo({
                    balance: pointsResult.data.balance || 0,
                    ledger: pointsResult.data.ledger || [],
                    purchases: pointsResult.data.purchases || [],
                    inventory: pointsResult.data.inventory || [],
                    inventory_history: pointsResult.data.inventory_history || [],
                });
                setShopItems(shopResult.data.items || []);
            } catch (err) {
                console.error("Shop load error:", err.response ? err.response.data : err.message);
                setError("Не вдалося завантажити магазин");
            } finally {
                setLoading(false);
            }
        };

        loadShop();
    }, [user, setUser, navigate]);

    const reloadPoints = async () => {
        const API_BASE = process.env.REACT_APP_API_BASE || "/api";
        const res = await axios.get(`${API_BASE}/user/${activeUser.telegram_id}/points`);
        setPointsInfo({
            balance: res.data.balance || 0,
            ledger: res.data.ledger || [],
            purchases: res.data.purchases || [],
            inventory: res.data.inventory || [],
            inventory_history: res.data.inventory_history || [],
        });
    };

    const copyTelegramId = async () => {
        try {
            await navigator.clipboard.writeText(String(activeUser.telegram_id));
            setCopyStatus("ID скопійовано");
            window.Telegram?.WebApp?.HapticFeedback?.selectionChanged?.();
        } catch {
            setCopyStatus("Не вдалося скопіювати");
        }
        window.setTimeout(() => setCopyStatus(""), 1600);
    };

    const buyShopItem = async (itemKey) => {
        try {
            setShopMessage("");
            setShopLoading(true);
            const API_BASE = process.env.REACT_APP_API_BASE || "/api";
            const res = await axios.post(`${API_BASE}/shop/purchase`, {
                telegram_id: activeUser.telegram_id,
                item_key: itemKey,
            });
            setShopMessage(res.data.message || "Покупку оформлено");
            setActiveTab("inventory");
            await reloadPoints();
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("success");
        } catch (err) {
            const msg = err.response?.data?.detail || err.message || "Помилка покупки";
            setShopMessage(msg);
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("error");
        } finally {
            setShopLoading(false);
        }
    };

    const activateInventoryItem = async (inventoryId) => {
        try {
            setShopMessage("");
            setShopLoading(true);
            const API_BASE = process.env.REACT_APP_API_BASE || "/api";
            const res = await axios.post(`${API_BASE}/user/${activeUser.telegram_id}/inventory/${inventoryId}/activate`);
            setShopMessage(res.data.message || "Предмет активовано");
            await reloadPoints();
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("success");
        } catch (err) {
            const msg = err.response?.data?.detail || err.message || "Не вдалося активувати предмет";
            setShopMessage(msg);
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("error");
        } finally {
            setShopLoading(false);
        }
    };

    const grantAdminTestBonus = async () => {
        if (!isAdmin || shopLoading) return;
        try {
            setShopMessage("");
            setShopLoading(true);
            const API_BASE = process.env.REACT_APP_API_BASE || "/api";
            const res = await axios.post(`${API_BASE}/admin/test-bonus?telegram_id=${activeUser.telegram_id}`);
            setShopMessage(res.data.message || "+1000 бонусів додано");
            await reloadPoints();
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("success");
        } catch (err) {
            const msg = err.response?.data?.detail || err.message || "Не вдалося додати бонуси";
            setShopMessage(msg);
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("error");
        } finally {
            setShopLoading(false);
        }
    };

    const renderInventory = () => (
        <div className={styles.inventoryList}>
            {inventoryItems.length ? inventoryItems.map((item) => {
                const catalogItem = shopItemByKey[item.item_key] || {};
                const title = item.meta?.title || catalogItem.title || item.item_key;
                const visual = getShopItemVisual(item.item_key);
                const canActivate = item.status === "available" && catalogItem.activation === "extended_analysis";
                const autoUseLabel = item.status === "available" && catalogItem.activation === "auto_extra_live"
                    ? "Списується автоматично після ліміту"
                    : "";
                return (
                    <div key={item.id} className={styles.inventoryItem}>
                        <div className={styles.inventoryInfo}>
                            <span className={`${styles.inventoryArt} ${styles[visual.artClass]}`} aria-hidden="true">
                                {visual.glyph}
                            </span>
                            <div>
                                <strong>{title}</strong>
                                <span>{formatInventoryStatus(item.status)}</span>
                                {(item.meta?.used_label || autoUseLabel) && <small>{item.meta?.used_label || autoUseLabel}</small>}
                            </div>
                        </div>
                        {canActivate ? (
                            <button
                                type="button"
                                onClick={() => activateInventoryItem(item.id)}
                                disabled={shopLoading}
                            >
                                Заюзати
                            </button>
                        ) : (
                            <em>{item.points_spent} балів</em>
                        )}
                    </div>
                );
            }) : (
                <div className={styles.emptyBonus}>Інвентар порожній. Купи предмет у магазині, і він з’явиться тут.</div>
            )}
        </div>
    );

    const renderHistory = () => {
        const ledger = safeArray(pointsInfo.ledger).slice(0, 12);
        return (
            <div className={styles.historyStack}>
                {inventoryHistoryItems.length > 0 && (
                    <>
                        <div className={styles.historyTitle}>Предмети</div>
                        <div className={styles.inventoryHistoryList}>
                            {inventoryHistoryItems.slice(0, 12).map((item) => {
                                const catalogItem = shopItemByKey[item.item_key] || {};
                                const title = item.meta?.title || catalogItem.title || item.item_key;
                                const visual = getShopItemVisual(item.item_key);
                                const eventDate = item.used_at || item.processed_at || item.created_at;
                                return (
                                    <div key={`inventory-history-${item.id}`} className={styles.inventoryHistoryRow}>
                                        <span className={`${styles.inventoryArt} ${styles[visual.artClass]}`} aria-hidden="true">
                                            {visual.glyph}
                                        </span>
                                        <div>
                                            <strong>{title}</strong>
                                            <small>{item.meta?.used_label || formatInventoryStatus(item.status)}</small>
                                        </div>
                                        <em>{formatInventoryDate(eventDate)}</em>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                <div className={styles.historyTitle}>Рух балів</div>
                <div className={styles.ledgerList}>
                    {ledger.map((entry) => (
                        <div key={entry.id} className={styles.ledgerRow}>
                            <span>{formatLedgerReason(entry)}</span>
                            <strong className={entry.delta >= 0 ? styles.pointsPlus : styles.pointsMinus}>
                                {entry.delta > 0 ? `+${entry.delta}` : entry.delta}
                            </strong>
                        </div>
                    ))}
                    {!ledger.length && !inventoryHistoryItems.length && (
                        <div className={styles.emptyBonus}>Історія порожня. Забирай щоденний бонус або купи предмет у магазині.</div>
                    )}
                </div>
            </div>
        );
    };

    if (loading) {
        return <div className={styles.statePage}><div className={styles.loader}/><span>Завантажуємо магазин</span></div>;
    }
    if (error) {
        return <div className={styles.statePage}><span className={styles.errorText}>{error}</span></div>;
    }

    return (
        <div className={styles.page}>
            <div className={styles.ambientBackground}/>
            <main className={styles.profileShell}>
                <header className={styles.topBar}>
                    <button onClick={() => navigate(-1)} className={styles.backButton}>← Назад</button>
                    <div className={styles.brandMark}><span>GRACH</span><small>POINTS SHOP</small></div>
                </header>

                <section className={styles.bonusPanel}>
                    <div className={styles.bonusHeader}>
                        <div><small>REFERRAL</small><h2>Запроси друга</h2></div>
                        <strong>{pointsInfo.balance}</strong>
                    </div>
                    <div className={styles.referralBox}>
                        <span>Друг має вступити в Сім’ю повністю: зареєструватись по рефці і зробити деп. У заявці на доступ він вводить твій Telegram ID, а після ручної перевірки ти отримуєш +1500 балів.</span>
                        <button type="button" onClick={copyTelegramId}>
                            <b>{activeUser.telegram_id}</b>
                            <small>{copyStatus || "Скопіювати мій ID"}</small>
                        </button>
                    </div>
                </section>

                <section className={styles.bonusPanel}>
                    <div className={styles.bonusHeader}>
                        <div><small>POINTS</small><h2>Бонуси</h2></div>
                        <strong>{pointsInfo.balance}</strong>
                    </div>

                    <div className={styles.shopTabs}>
                        <button
                            type="button"
                            className={activeTab === "shop" ? styles.shopTabActive : ""}
                            onClick={() => setActiveTab("shop")}
                        >
                            Магазин
                        </button>
                        <button
                            type="button"
                            className={activeTab === "inventory" ? styles.shopTabActive : ""}
                            onClick={() => setActiveTab("inventory")}
                        >
                            Інвентар
                        </button>
                        <button
                            type="button"
                            className={activeTab === "history" ? styles.shopTabActive : ""}
                            onClick={() => setActiveTab("history")}
                        >
                            Історія
                        </button>
                    </div>

                    {isAdmin && (
                        <button
                            type="button"
                            className={styles.adminBonusButton}
                            onClick={grantAdminTestBonus}
                            disabled={shopLoading}
                        >
                            +1000 бонусів
                        </button>
                    )}

                    {shopMessage && <div className={styles.shopMessage}>{shopMessage}</div>}

                    {activeTab === "shop" && (
                        <div className={styles.shopGrid}>
                            {shopItems.map((item) => {
                                const disabled = shopLoading || pointsInfo.balance < item.price;
                                const visual = getShopItemVisual(item.key);
                                return (
                                    <button
                                        key={item.key}
                                        type="button"
                                        className={styles.shopItem}
                                        onClick={() => buyShopItem(item.key)}
                                        disabled={disabled}
                                    >
                                        <span className={`${styles.shopItemArt} ${styles[visual.artClass]}`} aria-hidden="true">
                                            <i>{visual.glyph}</i>
                                            <em>{visual.badge}</em>
                                        </span>
                                        <span className={styles.shopItemTitle}>{item.title}</span>
                                        <small>{item.description}</small>
                                        <span className={styles.shopItemFooter}>
                                            <b>{item.price} балів</b>
                                            <em>{disabled ? "Не вистачає" : "Купити"}</em>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {activeTab === "inventory" && (
                        renderInventory()
                    )}

                    {activeTab === "history" && (
                        renderHistory()
                    )}
                </section>
            </main>
        </div>
    );
}
